import { API_URL } from 'src/lib/api-client';

/** Called with an event's parsed JSON. */
export type EventHandler = (data: any) => void;

export interface EventStreamOptions {
  /** The access token to connect with; read again on every reconnect. */
  getToken: () => string;
  /** Handlers by event name (`ReceiveNotification`, `UpdateUnreadCount`, ...). */
  handlers: Record<string, EventHandler>;
  /** The stream is connected (also after a reconnect). */
  onOpen?: () => void;
  /** The server refused the token: the stream is closed and won't reconnect. */
  onUnauthorized?: () => void;
}

const RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];

/** One `event:` / `data:` block of a server-sent-events stream. */
export function parseEvent(block: string): { name: string; data: string } | null {
  let name = 'message';
  const data: string[] = [];
  block.split('\n').forEach((raw) => {
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    // A line starting with a colon is a comment: the server's keep-alive.
    if (!line || line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') name = value;
    if (field === 'data') data.push(value);
  });
  return data.length ? { name, data: data.join('\n') } : null;
}

/**
 * The signed-in user's live events (`GET /events`, server-sent events). `EventSource`
 * can't send an `Authorization` header, so the stream is read with `fetch`. It
 * reconnects by itself until the returned function is called.
 */
export function openEventStream(options: EventStreamOptions): () => void {
  const controller = new AbortController();
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  const dispatch = (block: string) => {
    const event = parseEvent(block);
    if (!event) return;
    if (event.name === 'ready') {
      attempt = 0;
      options.onOpen?.();
      return;
    }
    const handler = options.handlers[event.name];
    if (!handler) return;
    try {
      handler(JSON.parse(event.data));
    } catch (error) {
      console.error(`Could not handle the ${event.name} event:`, error);
    }
  };

  const read = async () => {
    const response = await fetch(`${API_URL}/v2/events`, {
      headers: { Accept: 'text/event-stream', Authorization: `Bearer ${options.getToken()}` },
      signal: controller.signal,
      cache: 'no-store',
    });
    if (response.status === 401 || response.status === 403) {
      controller.abort();
      options.onUnauthorized?.();
      return;
    }
    if (!response.ok || !response.body) {
      throw new Error(`Event stream answered ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() ?? '';
      blocks.forEach(dispatch);
    }
  };

  const connect = () => {
    read()
      .catch((error) => {
        if (!controller.signal.aborted) console.warn('Event stream interrupted:', error);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      });
  };

  connect();

  return () => {
    if (retryTimer) clearTimeout(retryTimer);
    controller.abort();
  };
}
